import { useCallback, useMemo } from 'react';
import { useModuleName } from 'hooks';
import { MODULE_NAME as CAMPAIGNS_MODULE_NAME } from 'modules/campaigns/config';
import { BUDGET_TYPES } from 'modules/campaigns/modules/common-campaign-form/config';
import { MODULE_NAME as ONBOARDING_MODULE_NAME } from 'modules/onboarding';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { useCampaignsApi } from 'services/api';
import { CHAT_DIALOG_MESSAGE_PROP_PATH } from '../config/chatDialogMessagePropPath';
import { getChatModalComponent } from '../transformers/getChatModalComponent';
import { useChatAgentUtils } from './useChatAgentUtils';
import styles from './useCampaignTools.module.scss';

const budgetStyles = {
    fontSize: styles.budgetFontSize,
    fontWeight: styles.budgetFontWeight,
    justifyContent: 'center',
    display: 'flex',
};

export const useCampaignsTools = ({ closeChat, sendResponseToChatAgent, addRecommendation } = {}) => {
    const moduleName = useModuleName();
    const account = useAccount();
    const campaignsApi = useCampaignsApi();
    const { formatMessage, formatNumber } = useIntl();
    const { setFormData } = useChatAgentUtils();

    const setBudgetValue = useCallback(
        ({ budgetDesktop, budgetMobile }) => {
            if (moduleName === ONBOARDING_MODULE_NAME) {
                setFormData({
                    // TODO: define the value according to the entity structure
                    budget: { type: BUDGET_TYPES.DAILY, value: budgetDesktop || budgetMobile },
                });
                closeChat();
                return true;
            }

            if (moduleName === CAMPAIGNS_MODULE_NAME) {
                setFormData({
                    budget: { type: BUDGET_TYPES.DAILY, value: budgetDesktop || budgetMobile },
                });
                closeChat();
                return true;
            }

            return false;
        },
        [closeChat, moduleName, setFormData]
    );

    const updateBidByPercentage = useCallback(
        async ({ campaignId, bid, increaseBidPercentage }) => {
            if (moduleName === CAMPAIGNS_MODULE_NAME) {
                try {
                    await campaignsApi.updateCampaign({
                        accountId: account.accountId,
                        campaignId,
                        changes: { bid: bid * (1 + increaseBidPercentage / 100) },
                    });
                    closeChat();
                    return true;
                } catch (error) {
                    const errorMessage = getChatModalComponent({
                        header: formatMessage({
                            id: 'abby.bid.update.error.modal.header',
                            defaultMessage:
                                'Something went wrong while updating your bid. Please try again or contact support@taboola.com if the issue persists.',
                        }),
                        buttonText: formatMessage({
                            id: 'abby.bid.update.retry.modal.button',
                            defaultMessage: 'Retry',
                        }),
                        body: (
                            <span style={budgetStyles}>
                                {formatNumber(increaseBidPercentage / 100, {
                                    style: 'percent',
                                    maximumFractionDigits: 0,
                                    signDisplay: 'exceptZero',
                                })}
                            </span>
                        ),
                        methodName: 'campaigns.updateBidByPercentage',
                        args: { bid, increaseBidPercentage, campaignId },
                    });
                    sendResponseToChatAgent({ [CHAT_DIALOG_MESSAGE_PROP_PATH]: errorMessage, actions: [] });
                    return false;
                }
            }

            return false;
        },
        [account.accountId, campaignsApi, formatMessage, formatNumber, sendResponseToChatAgent, closeChat, moduleName]
    );

    const setBudgetRecommendation = useCallback(
        ({ budgetDesktop, budgetMobile } = {}) => {
            addRecommendation('budget', { type: BUDGET_TYPES.DAILY, value: budgetDesktop || budgetMobile });
            return true;
        },
        [addRecommendation]
    );

    const setBudgetComponent = useCallback(
        ({ budgetDesktop, budgetMobile, ...rest }) => {
            const message = getChatModalComponent({
                header: formatMessage({
                    id: 'abby.budget.modal.header',
                    defaultMessage: "Abby's suggestion for daily budget",
                }),
                buttonText: formatMessage({ id: 'abby.budget.modal.button', defaultMessage: 'Apply to form' }),
                body: (
                    <span style={budgetStyles}>
                        {formatNumber(budgetDesktop || budgetMobile, {
                            style: 'currency',
                            currency: account.currency,
                        })}
                    </span>
                ),
                methodName: 'campaigns.setBudgetValue',
                args: { budgetDesktop, budgetMobile, ...rest },
            });
            sendResponseToChatAgent({ [CHAT_DIALOG_MESSAGE_PROP_PATH]: message, actions: [] });
        },
        [account.currency, formatMessage, formatNumber, sendResponseToChatAgent]
    );

    const increaseBidByPercentageComponent = useCallback(
        async ({ campaignId, increaseBidPercentage, ...rest }) => {
            let campaign = await campaignsApi.getCampaign({
                accountId: account.accountId,
                campaignId,
            });
            const message = getChatModalComponent({
                header: formatMessage({
                    id: 'abby.bid.modal.header',
                    defaultMessage: "Abby's suggestion for CPC bid",
                }),
                buttonText: formatMessage({ id: 'abby.bid.modal.button', defaultMessage: 'Apply bid increase' }),
                body: (
                    <span style={budgetStyles}>
                        {formatNumber(increaseBidPercentage / 100, {
                            style: 'percent',
                            maximumFractionDigits: 0,
                            signDisplay: 'exceptZero',
                        })}
                    </span>
                ),
                methodName: 'campaigns.updateBidByPercentage',
                args: { increaseBidPercentage, campaignId, bid: campaign.bid, ...rest },
            });
            sendResponseToChatAgent({ [CHAT_DIALOG_MESSAGE_PROP_PATH]: message, actions: [] });
        },
        [account.accountId, campaignsApi, formatMessage, formatNumber, sendResponseToChatAgent]
    );

    return useMemo(
        () => ({
            setBudgetComponent,
            setBudgetValue,
            setBudgetRecommendation,
            updateBidByPercentage,
            increaseBidByPercentageComponent,
        }),
        [
            setBudgetComponent,
            setBudgetValue,
            setBudgetRecommendation,
            updateBidByPercentage,
            increaseBidByPercentageComponent,
        ]
    );
};
